import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Coins } from "lucide-react";
import {
  Button,
  DetailSection,
  EmptyState,
  ListCard,
  SkeletonList,
  SkeletonText,
  StatusBadge,
  Textarea,
  Timestamp,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import {
  tpsDistributionsApi,
  type TpsDistribution,
  type TpsDistributionComment,
  type TpsDistributionLine,
} from "../api/tpsDistributions";
import {
  BatchConfirmDialog,
  BatchResultBanner,
  BatchRowCheckbox,
  BatchRowError,
  BatchSelectionHeader,
  useBatchApprove,
} from "../components/BatchApprove";

// TPS Servicing Portal PR-G — the firm's "Distribution Ledger". Each row is a
// cleared payment's waterfall breakdown; the firm reviews the payees and
// APPROVES it (the second hard gate), which fans out the Connect payouts out of
// its clearing account. A blocked approve (funds not cleared / a payee not
// onboarded) returns HTTP 400 with the reason; the reason is also stamped on the
// distribution so the refreshed ledger shows why it's stuck. No React Query in
// the portal; load is plain useState and approve re-runs the list loader.

const STATUS_TONE: Record<string, StatusTone> = {
  Pending: "warn",
  Approved: "progress",
  Paid: "ok",
  Reversed: "danger",
  Blocked: "danger",
  // Distribution Gate PR-3 — the TPS sent the payout back for correction.
  Returned: "danger",
};

function statusTone(status: string): StatusTone {
  return STATUS_TONE[status] ?? "neutral";
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

/** "2026-06-01" → "June 2026" (the distribution period). */
function periodLabel(periodStart: string): string {
  const d = new Date(`${periodStart}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return periodStart;
  return d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

export function TpsDistributionLedgerTab() {
  const toast = useToast();
  const [rows, setRows] = useState<TpsDistribution[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const openDistribution = params.get("distribution");

  const loadList = useCallback(async () => {
    const data = await tpsDistributionsApi.list();
    setRows(data);
    return data;
  }, []);

  // SP-2b — checkbox batch approve over the Pending distributions (blocked
  // ones stay selectable; the server reports the block reason per item).
  const batch = useBatchApprove({
    approve: (ids) => tpsDistributionsApi.batchApprove(ids),
    refresh: loadList,
    successText: (n) =>
      `All ${n} distribution${n === 1 ? "" : "s"} approved and paid out.`,
    onRequestError: (err) =>
      toast.error(errorMessage(err, "Couldn't approve the selected distributions. Try again.")),
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsDistributionsApi.list();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your distributions."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOpenDistribution = (id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set("distribution", id);
    else updated.delete("distribution");
    setParams(updated, { replace: true });
  };

  if (openDistribution) {
    return (
      <DistributionDetail
        distributionId={openDistribution}
        onBack={() => setOpenDistribution(null)}
        refreshList={loadList}
      />
    );
  }

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }

  if (!rows) {
    return <SkeletonList rows={4} data-testid="tps-distributions-loading" />;
  }

  const selectableIds = rows.filter((dist) => dist.status === "Pending").map((dist) => dist.id);
  const approveLabel = `Approve & pay out ${batch.selected.size}`;

  return (
    <section aria-label="Distributions" className="flex flex-col gap-space-4">
      <BatchResultBanner batch={batch} testIdPrefix="tps-distributions-batch" />
      {rows.length === 0 ? (
        <EmptyState
          icon={Coins}
          title="No distributions yet"
          description="Once a borrower payment clears, its distribution breakdown will appear here for you to approve. Funds pay out of your clearing account on approval."
        />
      ) : (
        <>
          <BatchSelectionHeader
            batch={batch}
            selectableIds={selectableIds}
            selectAllLabel="Select all awaiting approval"
            actionLabel={approveLabel}
            testIdPrefix="tps-distributions-batch"
          />
          <div className="flex flex-col gap-space-3">
            {rows.map((dist) => (
              <div key={dist.id} className="flex flex-col gap-space-1">
                <div className="flex items-start gap-space-2">
                  {dist.status === "Pending" && (
                    <BatchRowCheckbox
                      id={dist.id}
                      batch={batch}
                      label={`Select distribution ${dist.loanNumber} — ${periodLabel(dist.periodStart)}`}
                      testIdPrefix="tps-distributions-batch"
                    />
                  )}
                  <ListCard
                    className="min-w-0 flex-1"
                    data-testid={`tps-distribution-row-${dist.id}`}
              title={`${dist.loanNumber} — ${periodLabel(dist.periodStart)}`}
              status={<StatusBadge tone={statusTone(dist.status)}>{dist.status}</StatusBadge>}
              meta={
                <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
                  <span>{formatCurrency(dist.totalAmount)}</span>
                  <span aria-hidden>·</span>
                  <span>
                    {dist.payeeCount} payee{dist.payeeCount === 1 ? "" : "s"}
                  </span>
                  {dist.fundsClearedAt && (
                    <>
                      <span aria-hidden>·</span>
                      <span>
                        Cleared <Timestamp value={dist.fundsClearedAt} />
                      </span>
                    </>
                  )}
                  {dist.blockReason && (
                    <>
                      <span aria-hidden>·</span>
                      <span className="text-status-error">Blocked: {dist.blockReason}</span>
                    </>
                  )}
                </span>
              }
                    actions={
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setOpenDistribution(dist.id)}
                        data-testid={`tps-distribution-open-${dist.id}`}
                      >
                        {dist.status === "Pending" ? "Review" : "View"}
                      </Button>
                    }
                  />
                </div>
                <BatchRowError
                  error={batch.rowErrors.get(dist.id)}
                  testId={`tps-distributions-batch-error-${dist.id}`}
                />
              </div>
            ))}
          </div>
          <BatchConfirmDialog
            batch={batch}
            title="Approve & pay out distributions"
            body={`Approve ${batch.selected.size} distribution${
              batch.selected.size === 1 ? "" : "s"
            }? Funds pay out of your clearing account on approval.`}
            confirmLabel={approveLabel}
            testIdPrefix="tps-distributions-batch"
          />
        </>
      )}
    </section>
  );
}

function DistributionDetail({
  distributionId,
  onBack,
  refreshList,
}: {
  distributionId: string;
  onBack: () => void;
  refreshList: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [summary, setSummary] = useState<TpsDistribution | null>(null);
  const [lines, setLines] = useState<TpsDistributionLine[] | null>(null);
  const [comments, setComments] = useState<TpsDistributionComment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [returning, setReturning] = useState(false);
  const [note, setNote] = useState("");

  const loadDetail = useCallback(async () => {
    const data = await tpsDistributionsApi.get(distributionId);
    setSummary(data.summary);
    setLines(data.lines);
    setComments(data.comments ?? []);
    return data;
  }, [distributionId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsDistributionsApi.get(distributionId);
        if (!cancelled) {
          setSummary(data.summary);
          setLines(data.lines);
          setComments(data.comments ?? []);
        }
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load this distribution."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [distributionId]);

  const approve = useSubmittingMutation(() => tpsDistributionsApi.approve(distributionId), {
    // Refresh both the list (status/blockReason may change) and this detail.
    refresh: [refreshList, loadDetail],
    onSuccess: () => {
      toast.success("Distribution approved and paid out.");
    },
    onError: (err) => toast.error(errorMessage(err, "Couldn't approve this distribution.")),
  });

  const submitReturn = useSubmittingMutation(
    () => tpsDistributionsApi.return(distributionId, note),
    {
      // Refresh the list (status changes) and fold the returned detail + thread.
      refresh: [refreshList, loadDetail],
      onSuccess: () => {
        setReturning(false);
        setNote("");
        toast.success("Returned to Phoenix for correction.");
      },
      onError: (err) =>
        toast.error(errorMessage(err, "Couldn't return this distribution. Try again.")),
    },
  );

  const backButton = (
    <Button
      type="button"
      variant="text"
      size="sm"
      onClick={onBack}
      data-testid="tps-distribution-back"
    >
      <ArrowLeft size={16} aria-hidden />
      Back to distributions
    </Button>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <p className="text-small text-status-error" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!summary || !lines) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <DetailSection>
          <SkeletonText lines={6} />
        </DetailSection>
      </div>
    );
  }

  const actionable = summary.status === "Pending";
  const pending = approve.pending || submitReturn.pending;

  return (
    <section
      aria-label={`Distribution for ${summary.loanNumber}, ${periodLabel(summary.periodStart)}`}
      className="flex flex-col gap-space-4"
    >
      <div className="flex items-center justify-between gap-space-3">
        {backButton}
        <StatusBadge tone={statusTone(summary.status)}>{summary.status}</StatusBadge>
      </div>

      <div>
        <h2 className="m-0 font-display text-h3 font-bold text-onyx">
          {summary.loanNumber} — {periodLabel(summary.periodStart)}
        </h2>
        <p className="mt-space-1 text-small text-gray-mid">
          {formatCurrency(summary.totalAmount)}
          {summary.fundsClearedAt && (
            <>
              {" · Funds cleared "}
              <Timestamp value={summary.fundsClearedAt} />
            </>
          )}
          {summary.approvedAt && (
            <>
              {" · Approved "}
              <Timestamp value={summary.approvedAt} />
            </>
          )}
          {summary.paidAt && (
            <>
              {" · Paid "}
              <Timestamp value={summary.paidAt} />
            </>
          )}
        </p>
      </div>

      {summary.blockReason && (
        <div
          role="note"
          className="rounded-md border border-status-error bg-status-error/10 p-space-3 text-small text-onyx"
          data-testid="tps-distribution-block-reason"
        >
          <span className="font-semibold">Can't approve yet:</span> {summary.blockReason}
        </div>
      )}

      {summary.status === "Returned" && summary.returnedReason && (
        <div
          role="note"
          className="rounded-md border border-status-error bg-status-error/10 p-space-3 text-small text-onyx"
          data-testid="tps-distribution-returned-reason"
        >
          <span className="font-semibold">Returned for correction:</span>{" "}
          {summary.returnedReason}
        </div>
      )}

      <DetailSection title="Payees">
        {lines.length === 0 ? (
          <p className="m-0 text-small text-gray-mid">No payees on this distribution.</p>
        ) : (
          <ul className="flex flex-col gap-space-2">
            {lines.map((line, i) => (
              <li
                key={`${line.recipientCounterpartyId}-${i}`}
                data-testid={`tps-distribution-line-${i}`}
                className="flex flex-wrap items-start justify-between gap-space-2 rounded-md border border-border-light bg-white-pure p-space-3"
              >
                <div className="min-w-0">
                  <div className="font-display font-semibold text-onyx">{line.recipientName}</div>
                  <div className="mt-space-1 flex flex-wrap items-center gap-x-space-2 gap-y-space-1 text-caption text-gray-mid">
                    <span className="uppercase tracking-label">{line.recipientKind}</span>
                    <span aria-hidden>·</span>
                    <span>{line.destination}</span>
                    <span aria-hidden>·</span>
                    <span>Account: {line.accountStatus}</span>
                    {line.transferStatus && (
                      <>
                        <span aria-hidden>·</span>
                        <span>Transfer: {line.transferStatus}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right font-display font-semibold text-onyx">
                  {formatCurrency(line.amount)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </DetailSection>

      {comments.length > 0 && (
        <DetailSection title="Conversation with Phoenix">
          <ul
            className="m-0 flex list-none flex-col gap-space-3 p-0"
            data-testid="tps-distribution-thread"
          >
            {comments.map((c) => (
              <li
                key={c.id}
                className="rounded-md border border-border-light bg-white-warm p-space-3"
                data-testid={`tps-distribution-comment-${c.id}`}
              >
                <p className="m-0 text-caption uppercase tracking-label text-gray-mid">
                  {c.authorName}
                  {" · "}
                  <Timestamp value={c.createdAt} />
                </p>
                <p className="m-0 mt-space-1 text-small text-onyx whitespace-pre-wrap">
                  {c.body}
                </p>
              </li>
            ))}
          </ul>
          <p className="mt-space-2 text-caption text-gray-mid">
            Returning this payout adds your note to the conversation; Phoenix
            replies here when they correct and resubmit it.
          </p>
        </DetailSection>
      )}

      {actionable && (
        <DetailSection title="Your decision">
          {returning ? (
            <div className="flex flex-col gap-space-3">
              <label className="flex flex-col gap-space-1">
                <span className="text-caption text-gray-mid uppercase tracking-label">
                  Reason for returning
                </span>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                  disabled={pending}
                  invalid={note.trim().length === 0}
                  aria-label="Reason for returning"
                  data-testid="tps-distribution-return-note"
                />
              </label>
              <div className="flex flex-wrap items-center gap-space-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => void submitReturn.submit()}
                  disabled={pending || note.trim().length === 0}
                  loading={submitReturn.pending}
                  data-testid="tps-distribution-return-submit"
                >
                  Return to Phoenix
                </Button>
                <Button
                  type="button"
                  variant="text"
                  size="sm"
                  onClick={() => {
                    setReturning(false);
                    setNote("");
                  }}
                  disabled={pending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-space-2">
              <div className="flex flex-wrap items-center gap-space-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => void approve.submit()}
                  disabled={pending || !summary.canApprove}
                  loading={approve.pending}
                  data-testid="tps-distribution-approve"
                >
                  Approve &amp; pay out
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setReturning(true)}
                  disabled={pending}
                  data-testid="tps-distribution-return"
                >
                  Return for correction
                </Button>
              </div>
              {!summary.canApprove && !summary.blockReason && (
                <p className="m-0 text-caption text-gray-mid">
                  This distribution can't be approved yet — you can still return it
                  for correction.
                </p>
              )}
            </div>
          )}
        </DetailSection>
      )}
    </section>
  );
}
